package link.erpango.quip.ui

import link.erpango.quip.utils.FileData
import link.erpango.quip.utils.FileUtils
import link.erpango.quip.utils.format

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.gestures.detectTapGestures
import coil3.compose.AsyncImage
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.AlertDialog
import androidx.compose.material.Card
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.Divider
import androidx.compose.material.FloatingActionButton
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Scaffold
import androidx.compose.material.Text
import androidx.compose.material.TextButton
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.List
import androidx.compose.material.icons.filled.Share
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.OutlinedTextField
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import link.erpango.quip.domain.entities.DSparePart
import link.erpango.quip.domain.entities.DSparePartDocument
import link.erpango.quip.features.maintenances.MaintenancesViewModel
import link.erpango.quip.ui.components.*
import link.erpango.quip.theme.QuipColors
import kotlinx.coroutines.launch
import link.erpango.quip.QuipConstants
import link.erpango.quip.utils.DatetimeUtils
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.Res
import quip.composeapp.generated.resources.add_document
import quip.composeapp.generated.resources.are_you_sure_delete_document
import quip.composeapp.generated.resources.brand
import quip.composeapp.generated.resources.cancel
import quip.composeapp.generated.resources.delete
import quip.composeapp.generated.resources.delete_document
import quip.composeapp.generated.resources.document_created_at
import quip.composeapp.generated.resources.document_name
import quip.composeapp.generated.resources.document_size
import quip.composeapp.generated.resources.document_updated_at
import quip.composeapp.generated.resources.documents
import quip.composeapp.generated.resources.info_label
import quip.composeapp.generated.resources.model
import quip.composeapp.generated.resources.reference_label
import quip.composeapp.generated.resources.spare_part_category
import quip.composeapp.generated.resources.spare_part_details
import quip.composeapp.generated.resources.spare_part_durability
import quip.composeapp.generated.resources.spare_part_durability_days
import quip.composeapp.generated.resources.spare_part_quantity
import quip.composeapp.generated.resources.spare_part_type

// Helper functions for translation keys (simplified for now)
/*@Composable
private fun getSparePartCategoryString(key: String): String {
    // In a real app, this would use stringResource with dynamic keys or a map
    return key.substringAfterLast('.')
}

@Composable
private fun getSparePartTypeString(key: String): String {
     // In a real app, this would use stringResource with dynamic keys or a map
    return key.substringAfterLast('.')
}*/
@Composable
fun SparePartDetailScreen(
    sparePart: DSparePart,
    viewModel: MaintenancesViewModel,
    onBack: () -> Unit
) {
    val documents by viewModel.sparePartDocuments.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val catalog by viewModel.sparePartCatalog.collectAsState()

    val scope = rememberCoroutineScope()
    var showDeleteDialog by remember { mutableStateOf<DSparePartDocument?>(null) }
    var documentDetails by remember { mutableStateOf<DSparePartDocument?>(null) }
    var documentToRename by remember { mutableStateOf<DSparePartDocument?>(null) }
    var newName by remember { mutableStateOf("") }

    val pickerLauncher = rememberCoroutineScope() // Placeholder to avoid breaking other code structure if we just removed it
    
    // Function to pick file
    fun pickFile() {
        scope.launch {
            val fileData = FileUtils.pickFile(
                allowedTypes = listOf("application/pdf", "image/png", "image/jpeg")
            )
            if (fileData != null) {
                viewModel.uploadSparePartDocument(sparePart, fileData)
            }
        }
    }

    LaunchedEffect(sparePart.id) {
        if (sparePart.id != null) {
            viewModel.loadSparePartDocuments(sparePart.id)
        }
    }

    // Rename Dialog removed - moved to Details Dialog


    Scaffold(
        floatingActionButton = {
            FloatingActionButton(
                onClick = {
                    pickFile()
                },
                backgroundColor = QuipColors.colorPrimary,
                contentColor = Color.White
            ) {
                Icon(Icons.Default.Add, contentDescription = stringResource(Res.string.add_document))
            }
        },
        backgroundColor = QuipColors.colorBackground
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            // Header
            QuipTransparentHeader {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = onBack) {
                        Icon(
                            Icons.Default.ArrowBack,
                            contentDescription = "Back",
                            tint = QuipColors.colorText
                        )
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = stringResource(Res.string.spare_part_details),
                        style = MaterialTheme.typography.h5,
                        fontWeight = FontWeight.Bold,
                        color = QuipColors.colorText
                    )
                }
            }

            LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Details Section
                item {
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        backgroundColor = QuipColors.colorSurface,
                        shape = RoundedCornerShape(12.dp),
                        elevation = 0.dp
                    ) {
                        Column(
                            modifier = Modifier.padding(16.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            DetailRow(stringResource(Res.string.spare_part_category), getSparePartCategoryString(catalog?.categories?.get(sparePart.categoryId) ?: sparePart.categoryId))
                            DetailRow(stringResource(Res.string.spare_part_type), catalog?.sparePartTypes?.get(sparePart.sparePartId)?.let { getSparePartTypeString(it.translationKey) } ?: sparePart.sparePartId)
                            DetailRow(stringResource(Res.string.brand), sparePart.brand)
                            DetailRow(
                                label = stringResource(Res.string.model),
                                value = sparePart.model
                            )
                            if (!sparePart.reference.isNullOrBlank()) {
                                DetailRow(stringResource(Res.string.reference_label), sparePart.reference)
                            }
                            if (sparePart.quantity > 0) {
                                DetailRow(stringResource(Res.string.spare_part_quantity), "${sparePart.quantity} ${sparePart.unit ?: ""}")
                            }
                            if (sparePart.durabilityDays != null && sparePart.durabilityDays > 0) {
                                DetailRow(stringResource(Res.string.spare_part_durability), stringResource(Res.string.spare_part_durability_days, sparePart.durabilityDays))
                            }
                            if (sparePart.additionalInfo.isNotBlank()) {
                                DetailRow(stringResource(Res.string.info_label), sparePart.additionalInfo)
                            }
                        }
                    }
                }

                item {
                     Text(
                        text = stringResource(Res.string.documents),
                        style = MaterialTheme.typography.h6,
                        fontWeight = FontWeight.Bold,
                        color = QuipColors.colorText,
                        modifier = Modifier.padding(vertical = 8.dp)
                    )
                }

                if (documents.isEmpty()) {
                    item {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(32.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = "No documents attached", // TODO: Resource
                                color = QuipColors.colorTextSecondary
                            )
                        }
                    }
                } else {
                    items(documents) { uiModel ->
                        val doc = uiModel.document
                        QuipDocumentItem(
                            name = doc.name,
                            fileType = doc.fileType,
                            filePath = doc.filePath,
                            sizeMb = doc.sizeMb,
                            dateFormatted = link.erpango.quip.utils.DatetimeUtils.dateToSpecificFormat(doc.createdAt, link.erpango.quip.QuipConstants.DateFormat.noUtcPattern1),
                            syncStatus = uiModel.status,
                            onShare = { viewModel.shareSparePartDocument(doc) },
                            onDelete = { showDeleteDialog = doc },
                            onClick = { 
                                viewModel.openSparePartDocument(doc)
                            },
                            onLongClick = {
                                documentDetails = doc
                            },
                            onUpload = { viewModel.retryUploadSparePartDocument(doc) },
                            onDownload = { viewModel.downloadSparePartDocument(doc) }
                        )
                    }
                }
                
                item {
                    Spacer(modifier = Modifier.height(80.dp)) // Space for FAB
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = QuipColors.colorPrimary)
            }
        }

        // Delete Dialog
        if (showDeleteDialog != null) {
            AlertDialog(
                onDismissRequest = { showDeleteDialog = null },
                title = { Text(stringResource(Res.string.delete_document)) },
                text = { Text(stringResource(Res.string.are_you_sure_delete_document)) },
                confirmButton = {
                    TextButton(
                        onClick = {
                            showDeleteDialog?.let { viewModel.deleteSparePartDocument(it) }
                            showDeleteDialog = null
                        }
                    ) {
                        Text(stringResource(Res.string.delete), color = QuipColors.colorError)
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showDeleteDialog = null }) {
                        Text(stringResource(Res.string.cancel), color = QuipColors.colorText)
                    }
                },
                backgroundColor = QuipColors.colorBackground,
                contentColor = QuipColors.colorText
            )
        }

        // Details Dialog
        if (documentDetails != null) {
            val doc = documentDetails!!
            QuipDialogGeneric(
                title = stringResource(Res.string.spare_part_details),
                content = {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .verticalScroll(rememberScrollState()),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        // Update newName state when documentDetails changes
                        LaunchedEffect(doc) {
                            newName = doc.name
                        }



                        // Name Edit
                        QuipTextField(
                            value = newName,
                            onValueChange = { newName = it },
                            label = stringResource(Res.string.document_name),
                            modifier = Modifier.fillMaxWidth()
                        )

                        // Size
                        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            Text(stringResource(Res.string.document_size), style = MaterialTheme.typography.caption, color = QuipColors.colorTextSecondary)
                            Text(
                                text = "${doc.sizeMb.format(2)} MB",
                                style = MaterialTheme.typography.body1,
                                color = QuipColors.colorText
                            )
                        }

                        // Created At
                        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            Text(stringResource(Res.string.document_created_at), style = MaterialTheme.typography.caption, color = QuipColors.colorTextSecondary)
                            Text(
                                text = DatetimeUtils.dateToSpecificFormat(doc.createdAt, link.erpango.quip.QuipConstants.DateFormat.noUtcPattern1),
                                style = MaterialTheme.typography.body1,
                                color = QuipColors.colorText
                            )
                        }
                        if (doc.updatedAt != null) {
                            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                                Text(stringResource(Res.string.document_updated_at), style = androidx.compose.material3.MaterialTheme.typography.bodySmall, color = QuipColors.colorTextSecondary)
                                Text(
                                    text = DatetimeUtils.dateToSpecificFormat(doc.updatedAt, QuipConstants.DateFormat.noUtcPattern1),
                                    style = androidx.compose.material3.MaterialTheme.typography.bodyLarge,
                                    color = QuipColors.colorText
                                )
                            }
                        }
                    }
                },
                confirmButtonText = "Save", // stringResource(Res.string.save)
                dismissButtonText = stringResource(Res.string.cancel),
                onConfirm = {
                    if (newName.isNotBlank() && newName != doc.name) {
                        viewModel.renameSparePartDocument(doc, newName)
                    }
                    documentDetails = null
                },
                onDismiss = { documentDetails = null }
            )
        }
    }
}

@Composable
fun DetailRow(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.body2,
            color = QuipColors.colorTextSecondary
        )
        Text(
            text = value,
            style = MaterialTheme.typography.body2,
            fontWeight = FontWeight.Medium,
            color = QuipColors.colorText
        )
    }
}


// Removed local DocumentItem as it is replaced by QuipDocumentItem
